import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { callsInbox, linkCallToDeal, listDeals, rematchInbox, type CallMatchSuggestion } from '../lib/api.js';
import { Card, PageHeader, Spinner, StatTile } from '../components/ui.js';

/**
 * "Unmatched calls" inbox — tl;dv meetings the ingest couldn't auto-match to a deal (title →
 * email domain → transcript mention all missed). An analyst links each to a deal here; linking
 * runs analysis (if not already done) and the call then shows on that deal's Calls tab.
 */
export function CallsInbox() {
  const qc = useQueryClient();
  const inboxQ = useQuery({ queryKey: ['callsInbox'], queryFn: callsInbox });
  const dealsQ = useQuery({ queryKey: ['deals'], queryFn: listDeals, staleTime: 60_000 });
  const [pick, setPick] = useState<Record<string, string>>({});

  const linkM = useMutation({
    mutationFn: ({ callId, dealId }: { callId: string; dealId: string }) => linkCallToDeal(callId, dealId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['callsInbox'] }),
  });

  const [suggested, setSuggested] = useState<Record<string, CallMatchSuggestion>>({});
  const [rematchNote, setRematchNote] = useState('');
  const rematchM = useMutation({
    mutationFn: rematchInbox,
    onSuccess: (r) => {
      setSuggested(Object.fromEntries(r.suggestions.map((s) => [s.callId, s])));
      setPick((p) => ({ ...p, ...Object.fromEntries(r.suggestions.map((s) => [s.callId, s.dealId])) }));
      setRematchNote(r.suggestions.length
        ? `${r.suggestions.length} of ${r.checked} calls have a suggested deal — review and click Link.`
        : `Checked ${r.checked} calls — no new matches.`);
    },
    onError: (e) => setRematchNote(String((e as Error).message ?? e)),
  });

  const calls = inboxQ.data?.calls ?? [];
  const deals = [...(dealsQ.data?.deals ?? [])].sort((a, b) => a.company.localeCompare(b.company));

  return (
    <div>
      <PageHeader
        eyebrow="Sourcing"
        title="Call Inbox"
        desc="tl;dv calls that couldn't be auto-matched to a deal. Pick the right deal to file each one."
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <StatTile label="Unmatched" value={calls.length} accent={calls.length ? 'amber' : 'green'} note={calls.length ? 'awaiting a deal' : 'all filed 🎉'} />
        <StatTile label="Analysed" value={calls.filter((c) => c.status === 'analysed').length} accent="teal" />
        <StatTile label="Deals" value={deals.length} accent="navy" note="to link into" />
      </div>

      <Card
        title="Unmatched calls"
        sub={calls.length ? 'link each to a deal' : undefined}
        actions={calls.length ? (
          <button
            type="button"
            disabled={rematchM.isPending}
            onClick={() => rematchM.mutate()}
            className="text-[12px] font-semibold text-teal-d border border-teal/40 hover:bg-teal/10 rounded-md px-3 py-1 disabled:opacity-50"
          >
            {rematchM.isPending ? 'Matching…' : 'Re-run matching'}
          </button>
        ) : undefined}
      >
        {rematchNote && <p className="text-[12px] text-muted-2 mb-2">{rematchNote}</p>}
        {inboxQ.isLoading ? (
          <div className="flex items-center gap-2 text-muted text-[12.5px] py-4"><Spinner size={14} /> Loading calls…</div>
        ) : inboxQ.error ? (
          <p className="text-[12.5px] text-red py-3">{String(inboxQ.error as Error)}</p>
        ) : calls.length === 0 ? (
          <div className="text-center text-muted text-[12.5px] py-8">No unmatched calls — everything from tl;dv landed on a deal. 🎉</div>
        ) : (
          <div className="flex flex-col divide-y divide-line">
            {calls.map((c) => (
              <div key={c.id} className="py-3 flex flex-col gap-1.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-navy truncate">{c.title}</div>
                    <div className="text-[11px] text-muted mt-0.5">
                      {[c.date && new Date(c.date).toLocaleDateString(), c.duration && `${c.duration}`, c.participants].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {c.recordingUrl && <a href={c.recordingUrl} target="_blank" rel="noreferrer" className="text-[11px] text-teal-d font-semibold hover:underline">▶ recording</a>}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.status === 'analysed' ? 'bg-teal/15 text-teal-d' : 'bg-canvas text-muted'}`}>{c.status}</span>
                  </div>
                </div>
                {c.summary && <p className="text-[11.5px] text-muted-2 line-clamp-2">{c.summary}</p>}
                <div className="flex items-center gap-2 mt-0.5">
                  <select
                    className="text-[12px] border border-line rounded-md px-2 py-1 bg-white max-w-[280px]"
                    value={pick[c.id] ?? ''}
                    onChange={(e) => setPick((p) => ({ ...p, [c.id]: e.target.value }))}
                  >
                    <option value="">Link to deal…</option>
                    {deals.map((d) => <option key={d.id} value={d.id}>{d.company}</option>)}
                  </select>
                  <button
                    type="button"
                    disabled={!pick[c.id] || (linkM.isPending && linkM.variables?.callId === c.id)}
                    onClick={() => linkM.mutate({ callId: c.id, dealId: pick[c.id]! })}
                    className="text-[12px] font-semibold text-white bg-teal hover:bg-teal-d rounded-md px-3 py-1 disabled:opacity-50"
                  >
                    {linkM.isPending && linkM.variables?.callId === c.id ? 'Linking…' : 'Link'}
                  </button>
                  {suggested[c.id] && (
                    <span className="text-[11px] text-teal-d">Suggested: {suggested[c.id]!.company} (via {suggested[c.id]!.method})</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
